import React, { useEffect, useState, useRef, ReactNode } from 'react';
import CollapseToggle from '../layout/CollapseToggle';
import Spacer from '../layout/Spacer';
import { WarningTriangleIcon } from '../Iconset';

interface IPropertyGroupProps
{
	/** a text to display at the header line of the group container. this text will always be formatted for display using the `TextUtils.prettifyText` function. */
	title: string,
	/** determines whether or not the group container should start in the expanded state. *defaults to `true`* */
	expanded?: boolean,
	/** an array of components to draw at the end (right side) of the group container's header line, e.g. an icon, button, text, etc. */
	decorators?: ReactNode,
	/** an additional CSS class name to apply to the root div */
	className?: string,
	/** an additional CSS class name to apply to the header table cell */
	headerClassName?: string,
	/** when set to `true` the group will always be in the expanded state */
	disableCollapse?: boolean,
	/** passed to the CSS to draw a special state when set to `true` */
	drawSelected?: boolean,
	/** style overrides to the root div */
	style?: React.CSSProperties,
	/** style overrides to the header table cell */
	headerStyle?: React.CSSProperties,
	/** the minimum height of **child property table rows** */
	minHeight?: number,
	/** CSS opacity applied to the **header table cells** */
	opacity_th?: number,
	/** when set to `true`, the group will be forced to the collapsed state */
	locked?: boolean,
	/** passed to the CSS to draw a special state when set to `true` */
	highlighted_th?: boolean,
	warning?: string,
	iconOverride?: React.ReactNode,
	onToggleCallback?: (v: boolean) => void,
	onContextMenu?: (e: React.MouseEvent<HTMLTableCellElement>) => void,
	onClick?: (e: React.MouseEvent<HTMLTableElement, MouseEvent>) => void,
	onClickOverride?: React.MouseEventHandler<HTMLTableCellElement>;
	onDoubleClick?: React.MouseEventHandler<HTMLTableCellElement>;
	onMouseEnter?: React.MouseEventHandler<HTMLTableCellElement>;
	onMouseLeave?: React.MouseEventHandler<HTMLTableCellElement>;
	"data-name"?: string,
	children?: ReactNode,
	"data-tooltip"?: string,
	/** DEPRECATED. Pass "data-tooltip" instead. */
	tooltip?: string,
	/** An icon to display before the title text */
	titleIcon?: ReactNode
}

/**
 * designed to act as a collapsible container for a collection of property components, but can be used for general purpose as well  
 * internally layouts children components as rows in a table, one per child component
 *
 * @export
 * @param {IPropertyGroupProps} props
 * @return {*} 
 */
export default function PropertyGroup(props: IPropertyGroupProps)
{
	const [isExpanded, setIsExpanded] = useState(!props.disableCollapse ? props.locked ? false : props.expanded ?? true : true);
	const toolTipRef = useRef<HTMLElement>(null);

	useEffect(() =>
	{
		if(props.locked === true && isExpanded)
		{
			setIsExpanded(false);
		}
		else if(props.locked !== true && props.expanded && !props.disableCollapse)
		{
			setIsExpanded(true);
		}
		else if(isExpanded && (props.disableCollapse || props.expanded === false))
		{
			setIsExpanded(false);
		} 
	}, [props.locked, props.expanded]);

	function onMouseEnterWarning(e)
	{
		if (toolTipRef && toolTipRef.current)
		{
			if (e)
			{
				const toolTipSize = toolTipRef.current.getBoundingClientRect();
				const iconSize = e.target.getBoundingClientRect();
				
				let xModifier = 0;
				let yModifier = (toolTipSize.height*-1) - 8;

				if(window.innerWidth - iconSize.x < toolTipSize.width + xModifier) xModifier -= (toolTipSize.width);
				if(window.innerHeight - iconSize.y < toolTipSize.height + yModifier) yModifier -= (toolTipSize.height + 15);

				const x = (iconSize.x + xModifier) + 'px', y = (iconSize.y + yModifier) + 'px';

				toolTipRef.current.style.top = y;
				toolTipRef.current.style.left = x;

				toolTipRef.current.style.visibility = 'visible';
			}
		}
	}

	function onMouseExitWarning(e)
	{
		if (toolTipRef && toolTipRef.current)
		{
			if (e)
			{
				toolTipRef.current.style.visibility = 'hidden';
			}
		}
	}

	function RenderChildProperties()
	{
		const childrenArray = React.Children.toArray(props.children);
		const result: React.ReactElement[] = [];
		for (let i = 0; i < childrenArray.length; i++)
		{
			const child = childrenArray[i];
			if (!child) continue;

			const buildStyle = {};
			if (props.minHeight)
			{
				buildStyle["minHeight"] = props.minHeight + 'px';
				buildStyle["height"] = props.minHeight + 'px';
			}

			result.push(
				<tr key={i} style={buildStyle}>
					<td>
						{
							React.isValidElement(child) ?
								React.cloneElement(child) :
								child
						}
					</td>
				</tr>
			);
		}

		return result;
	}

	return (
		<table className={[
			"propertyGroup",
			(props.className ? props.className : ""),
		].join(' ')}
		{...Object.fromEntries(Object.entries(props).filter(([key, value]) => key.includes("data-")))}
		data-name={props["data-name"] ? props["data-name"] + "-root" : "property-group-" + props.title + "-root"}
		data-drawselected={props.drawSelected}
		data-expanded={isExpanded ? "true" : "false"}
		onClickCapture={props.onClick}
		style={props.style}		
		>
			<thead>
				<tr>
					<th className={[
						"propertyGroupHeader",
						(props.headerClassName ? props.headerClassName : ""),
					].join(' ')}
					data-highlighted={props.highlighted_th ? "true" : "false"}
					style={props.opacity_th ? {opacity : props.opacity_th} : {}}
					onMouseEnter={props.onMouseEnter}
					onMouseLeave={props.onMouseLeave}
					onDoubleClick={props.onDoubleClick ? props.onDoubleClick : () =>
					{
						if(props.disableCollapse || props.locked) return;
						setIsExpanded(!isExpanded);
						if(props.onToggleCallback)
						{
							props.onToggleCallback(!isExpanded);
						}
					}}
					onClick={props.onClickOverride ? props.onClickOverride : undefined}
					onContextMenu={(e) =>
					{
						e.preventDefault();
						if(props.onContextMenu)
						{
							props.onContextMenu(e);
						}
					}}
					data-tooltip={props["data-tooltip"] ? props["data-tooltip"] : props.tooltip}
					>
						{
							!props.disableCollapse && !props.locked ?
								<CollapseToggle
									isExpanded={isExpanded}
									iconOverride={props.iconOverride}
									onToggleCallback={(v) =>
									{
										setIsExpanded(v);
										if(props.onToggleCallback)
										{
											props.onToggleCallback(v);
										}
									}}
								/> : <></>
						}
						<div className="propertyGroupHeaderText" data-locked={props.locked ? 'true' : 'false'} style={props.headerStyle}>
							{ props.titleIcon }
							{props.title}
						</div>
						{
							!props.warning ? null :
								<div className={"warningTriangleContainer"}>
									<WarningTriangleIcon 
										onClick={null} 
										draggable={true}
										onDragStart={(e)=>{e.preventDefault()}}
										className={"warningTrianglePropertyGroup"} 
										onMouseEnter={onMouseEnterWarning} 
										onMouseLeave={onMouseExitWarning}
										data-name={props["data-name"] ? props["data-name"] + "-warning-triangle-icon" : "synced-property-warning-icon"}
									/>
									<div className="tooltip cleanTooltip">
										<span className="tooltiptext" ref={toolTipRef}>{props.warning}</span>
									</div>
								</div>
						}
						<Spacer stretchTo={{ x: "100%" }} />
						{props.decorators}
					</th>
				</tr>
			</thead>
			<tbody>
				{
					!isExpanded ? null : RenderChildProperties()
				}
			</tbody>
		</table>
	);
}